import {
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  useMediaQuery,
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  Flex,
  useBreakpointValue,
} from "@chakra-ui/react";
import AddIcon from "components/Icons/TobarIcons/AddIcon";
import DeviceIcon from "components/Icons/TobarIcons/DeviceIcon";
import WindowsIcon from "components/Icons/TobarIcons/WindowsIcon";
import MobileGreenIcon from "components/Icons/TobarIcons/MobileGreenIcon";
import MobileGreyIcon from "components/Icons/TobarIcons/MobileGreyIcon";
import MobileRedIcon from "components/Icons/TobarIcons/MobileRedIcon";
import MobileYellowIcon from "components/Icons/TobarIcons/MobileYellowIcon";
import SuccessGreenIcon from "components/Icons/TobarIcons/SuccessGreenIcon";
import { useAppSelector } from "hooks/store/hooks";
import { useTranslation } from "TranslationContext";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import apiHelper from "services/apiHelper";
import {
  markAuthStore,
  selectCurrentDevice,
  selectCurrentDeviceStatusSettings,
  selectUserDetails,
} from "stores/auth/authSlice";
import {
  markUI,
  selectColorThemeMode,
  selectIsSidebarOpen,
} from "stores/ui/uiSlice";
import { getUserDetails, setCurrentDeviceDetails, setUserDetails } from "utils/local-storage";
import { NumberToTimeText, useDeviceDetection } from "utils/misc";
import MacGreenIcon from "components/Icons/TobarIcons/MacGreenIcon";
import MacYellowIcon from "components/Icons/TobarIcons/MacYellowIcon";
import MacGreyIcon from "components/Icons/TobarIcons/MacGreyIcon";
import MacRedIcon from "components/Icons/TobarIcons/MacRedIcon";
import WindowsGreenIcon from "components/Icons/TobarIcons/WindowsGreenIcon";
import WindowsYellowIcon from "components/Icons/TobarIcons/WindowsYellowIcon";
import WindowsGreyIcon from "components/Icons/TobarIcons/WindowsGreyIcon";
import WindowsRedIcon from "components/Icons/TobarIcons/WindowsRedIcon";
import MacWhiteIcon from "components/Icons/TobarIcons/MacWhiteIcon";
import WhatsAppYellowIcon from "components/Icons/WhatsAppWeb/WhatsAppYellowIcon";
import WhatsAppGreenIcon from "components/Icons/WhatsAppWeb/WhatsAppGreenIcon";
import WhatsAppRedIcon from "components/Icons/WhatsAppWeb/WhatsAppRedIcon";
import WhatsAppGreyIcon from "components/Icons/WhatsAppWeb/WhatsAppGreyIcon";
import WhatsAppWhiteIcon from "components/Icons/WhatsAppWeb/WhatsAppWhiteIcon";
import SearchIcon from "components/Icons/SidebarIcons/SearchIcon";
import WhiteRefreshIcon from "components/Icons/QuickAccessBarIcons/WhiteRefreshIcon";

const DeviceSelectionModal = () => {
  // Function to handle device selection
  const { isDesktop } = useDeviceDetection();
  const colorThemeMode = useAppSelector(selectColorThemeMode);
  const { translate } = useTranslation();
  const [searchValue, setSearchValue] = useState("");
  const isSidebarOpen = useAppSelector(selectIsSidebarOpen);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { devices } = useAppSelector(selectUserDetails);
  const currentDevice = useAppSelector(selectCurrentDevice);
  const currentDeviceStatusSettings = useAppSelector(
    selectCurrentDeviceStatusSettings
  );
  const getCurrentDeviceEventTime =
    currentDeviceStatusSettings?.device_status?.last_event1;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const userDetails = getUserDetails();
  const [loading, setLoading] = useState(false);
  
  const handleSelectDevice = async (newDevice) => {
    await apiHelper(
      `api/imei/updateimei/${newDevice.imei}/${currentDevice.imei}`,
      "get",
      null,
      {}
    )
      .then((res) => {
        setCurrentDeviceDetails(newDevice); // Update local state with new device details

        // Update the Redux store with the new selected device details
        dispatch(
          markAuthStore({
            currentDevice: newDevice,
            family: newDevice.family,
          })
        );

        const type = res.data.data.license.type; // Get device license type

        // Handle various response statuses and redirect accordingly
        if (res.data.status === 203) {
          dispatch(markAuthStore({ appDisable: false }));
          navigate("/registerNewDevice"); // Redirect to new device registration page
        } else if (res.data.status === 402) {
          dispatch(markAuthStore({ appDisable: true }));
          navigate("/subscription"); // Redirect to subscription page if payment is required
        } else if (
          type == null ||
          type === "" ||
          type === "3" ||
          type === "4"
        ) {
          dispatch(markAuthStore({ appDisable: true }));
          navigate("/subscription"); // Redirect to subscription if license type is invalid
        } else {
          dispatch(markAuthStore({ appDisable: false }));
          // Navigate based on device family type
          navigate(newDevice.family === 12 ? "/whatsappWebChat" : "/dashboard");
        }

        onClose(); // Close the modal/popover
      })
      .catch((err) => {
        // Handle errors, especially subscription-related issues
        if (err?.response?.data?.status === 402) {
          setCurrentDeviceDetails(newDevice);
          dispatch(
            markAuthStore({
              currentDevice: newDevice,
              family: newDevice.family,
              appDisable: true,
            })
          );
          navigate("/subscription"); // Redirect to subscription page on error
        }
        onClose(); // Ensure modal is closed even if an error occurs
      });
  };

  useEffect(() => {
    if (!isOpen) {
      setSearchValue("");
    }
  }, [isOpen]);

  const fetchdevicelistData = async () => {
      if (loading) return;
      setLoading(true);
      try {
        const res = await apiHelper(`api/user/devicelist`, "get", null, {});
        if(JSON.stringify(userDetails.devices) !== JSON.stringify(res.data.data)){
          userDetails.devices = res.data.data;
            setUserDetails(userDetails);
            let lastSelectedDevice = undefined;
            lastSelectedDevice = userDetails.devices.find(
              (item) => item.imei === currentDevice?.imei
            );
            if (lastSelectedDevice) {
              setCurrentDeviceDetails(lastSelectedDevice);
              dispatch(
                markAuthStore({
                  userDetails: {
                    email: userDetails?.email,
                    devices: userDetails?.devices,
                    email_status: userDetails?.email_status,
                  },
                  currentDevice: lastSelectedDevice,
                  family: lastSelectedDevice.family,
                })
              )
            }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
 
  };

  // Determine the device status color based on last event time
  if (currentDevice) {
    let statusColor = "green";
    if (devices.length === 0) {
      statusColor = "red"; // Set to red if no devices are available
    } else {
      const lasteventime =
        currentDevice.lasteventtime ?? getCurrentDeviceEventTime;
      const time = Date.now() - lasteventime;
      if (time <= 3000000) {
        statusColor = "green";
      } else if (time <= 6000000) {
        statusColor = "yellow";
      } else if (time <= 129600000) {
        statusColor = "grey";
      } else {
        statusColor = "red"; // Device is inactive for a very long time
      }
    }
    // Function to get the corresponding status icon based on device type (family) and status color
    const getStatusIcon = (family, statusColor) => {
      const icons = {
        1: {
          green: <MobileGreenIcon />,
          yellow: <MobileYellowIcon />,
          grey: <MobileGreyIcon />,
          red: <MobileRedIcon />,
        },
        7: {
          green: <WindowsGreenIcon />,
          yellow: <WindowsYellowIcon />,
          grey: <WindowsGreyIcon />,
          red: <WindowsRedIcon />,
        },
        9: {
          green: <MobileGreenIcon />,
          yellow: <MobileYellowIcon />,
          grey: <MobileGreyIcon />,
          red: <MobileRedIcon />,
        },
        11: {
          green: <MacGreenIcon />,
          yellow: <MacYellowIcon />,
          grey: <MacGreyIcon />,
          red: <MacRedIcon />,
        },
        12: {
          green: <WhatsAppGreenIcon />,
          yellow: <WhatsAppYellowIcon />,
          grey: <WhatsAppGreyIcon />,
          red: <WhatsAppRedIcon />,
        },
      };

      return icons[family]?.[statusColor] || null; // Return the matching icon or null if not found
    };

    return (
      <>
        <Button
          className="deviceSelect"
          width={"fit-content"}
          onClick={onOpen}
          p={{ base: "5px 20px", md: "5px 10px", lg: "5px 20px" }}
          h="50px"
          borderRadius={"0px"}
          _hover={{ bg: "#00000020" }}
          maxW={{ base: "auto", lg: "200px" }}
          overflow={"hidden"}
          display={"flex"}
          alignItems={"center"}
        >
          {/* {statusColor === "green" ? (
            <MobileGreenIcon />
          ) : statusColor === "yellow" ? (
            <MobileYellowIcon />
          ) : statusColor === "grey" ? (
            <MobileGreyIcon />
          ) : (
            <MobileRedIcon />
          )} */}

          {getStatusIcon(currentDevice.family, statusColor)}

          <Text
            display={{
              base: "block",
              md: isSidebarOpen ? "none" : "block",
              lg: "block",
            }}
            fontSize={"17px"}
            fontWeight={200}
            color={isDesktop==="computer" ? "#ffffffcc" : `${colorThemeMode}_text`}
            m="0px"
            ml="10px"
          >
            {devices.length === 0
              ? ""
              : currentDevice.friendlyname === ""
              ? currentDevice.model
              : currentDevice.friendlyname.replace(/\+/g, " ")}
            {/* {currentDevice.model === "" ? currentDevice.friendlyname : currentDevice.model} */}
          </Text>
        </Button>

        <Modal isOpen={isOpen} onClose={onClose} isCentered>
          <ModalOverlay />
          <ModalContent
            mx={{ base: "20px", sxm: "0px" }}
            minW={{
              base: "90% !important",
              sxm: "500px !important",
              lg: "798px !important",
            }}
          >
            <ModalHeader
              borderRadius={"4px 4px 0px 0px"}
              bg={`${colorThemeMode}_background`}
              color={`${colorThemeMode}_text`}
              borderBottom={"1px solid"}
              borderColor={`${colorThemeMode}_tertiary`}
            >
              <ModalCloseButton position="absolute" size={"sm"} top="8px" right="8px" />
              <Flex justify="space-between" align="center" w="100%" mt={"30px"}>
                <InputGroup maxW="calc(100% - 40px)">
                  <Input
                    type="text"
                    name="searchValue"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    color={`${colorThemeMode}_text`}
                    bg={`${colorThemeMode}_tertiary`}
                  />
                  <InputLeftElement>
                    <Button
                      p="0px"
                      bg="none"
                      h="38px"
                      fontSize="12px"
                      w="auto"
                      _hover={{}}
                      _active={{}}
                    >
                      <SearchIcon />
                    </Button>
                  </InputLeftElement>
                </InputGroup>
                <Button
                  w="42px"
                  h="38px"
                  ml={"10px"}
                  borderRadius={"4px"}
                  bg="#39c449"
                  onClick={fetchdevicelistData}
                  isDisabled={loading}
                  _hover={{ bg: "#39c449", }}
                  _disabled={{ bg: "#a5d6a7", cursor: "not-allowed", opacity: 0.7, }}
                >
                  <WhiteRefreshIcon />
                </Button>
              </Flex>
            </ModalHeader>
            <ModalBody
              borderRadius={"0px 0px 4px 4px"}
              bg={`${colorThemeMode}_background`}
              color={`${colorThemeMode}_text`}
              p="16px"
            >
              {searchValue.trim().length > 0
                ? devices
                    .filter((device) => {
                      const search = searchValue
                        .replace(/\s+/g, "")
                        .toLowerCase();

                      const friendly = (device.friendlyname || "").replace(
                        /\+/g,
                        " "
                      );
                      const model = device.model || "";

                      const nameToSearch = `${friendly} ${model}`
                        .replace(/\s+/g, "")
                        .toLowerCase();

                      return nameToSearch.includes(search);
                    })
                    .map((device) => {
                      let statusColor = "green";
                      const time = Date.now() - device.lasteventtime;
                      if (time <= 3000000) {
                        statusColor = "green";
                      } else if (time <= 6000000) {
                        statusColor = "yellow";
                      } else if (time <= 129600000) {
                        statusColor = "gray";
                      } else {
                        statusColor = "red";
                      }

                      return (
                        <Box
                          key={device.imei} // Ensure this is unique
                          bgColor={
                            device.license.type === 3
                              ? "#d8d8d8"
                              : device.license.type === 4
                              ? "#ffe9e9"
                              : undefined
                          }
                          display={"flex"}
                          alignItems={"center"}
                          justifyContent={"space-between"}
                          p="20px"
                          borderRadius={"4px"}
                          h={"100%"}
                          boxShadow={
                            "0 2px 9px 0 rgba(169, 184, 200, .2) !important"
                          }
                          mb="10px"
                          _hover={{ cursor: "pointer" }}
                          onClick={() => {
                            if (currentDevice.imei !== device.imei) {
                              handleSelectDevice(device);
                            }
                          }}
                        >
                          <Box display={"flex"} alignItems={"center"}>
                            <Box
                              minW={"40px"}
                              h={"40px"}
                              bgColor={statusColor}
                              borderRadius={"50px"}
                              display={"flex"}
                              alignItems={"center"}
                              justifyContent={"center"}
                            >
                              {device.family === 7 ? (
                                <WindowsIcon />
                              ) : device.family === 11 ? (
                                <MacWhiteIcon />
                              ) : device.family === 12 ? (
                                <WhatsAppWhiteIcon />
                              ) : (
                                <DeviceIcon />
                              )}
                            </Box>
                            <Box ml="10px" w="100%">
                              <Text fontSize={"18px"} fontWeight={400}>
                                {device.friendlyname === ""
                                  ? device.model
                                  : device.friendlyname.replace(/\+/g, " ")}
                              </Text>
                              {device.lasteventtime > 0 && (
                                <Text color={"#90a4ae"} fontWeight={300}>
                                  {`${
                                    translate["lastdevice"]
                                  } ${NumberToTimeText(device.lasteventtime)}`}
                                </Text>
                              )}
                            </Box>
                          </Box>
                          <Box
                            ml="10px"
                            display={"flex"}
                            h={"100%"}
                            alignItems={"center"}
                          >
                            {device.imei === currentDevice.imei && (
                              <SuccessGreenIcon />
                            )}
                          </Box>
                        </Box>
                      );
                    })
                : devices.map((device) => {
                    let statusColor = "green";
                    const time = Date.now() - device.lasteventtime;
                    if (time <= 3000000) {
                      statusColor = "green";
                    } else if (time <= 6000000) {
                      statusColor = "yellow";
                    } else if (time <= 129600000) {
                      statusColor = "gray";
                    } else {
                      statusColor = "red";
                    }

                    return (
                      <Box
                        key={device.imei} // Use IMEI instead of model for uniqueness
                        bgColor={
                          device.license.type === 3
                            ? "#d8d8d8"
                            : device.license.type === 4
                            ? "#ffe9e9"
                            : undefined
                        }
                        display={"flex"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                        p="20px"
                        borderRadius={"4px"}
                        h={"100%"}
                        boxShadow={
                          "0 2px 9px 0 rgba(169, 184, 200, .2) !important"
                        }
                        mb="10px"
                        _hover={{ cursor: "pointer" }}
                        onClick={() => {
                          if (currentDevice.imei !== device.imei) {
                            handleSelectDevice(device);
                          }
                        }}
                      >
                        <Box display={"flex"} alignItems={"center"}>
                          <Box
                            minW={"40px"}
                            h={"40px"}
                            bgColor={statusColor}
                            borderRadius={"50px"}
                            display={"flex"}
                            alignItems={"center"}
                            justifyContent={"center"}
                          >
                            {device.family === 7 ? (
                              <WindowsIcon />
                            ) : device.family === 11 ? (
                              <MacWhiteIcon />
                            ) : device.family === 12 ? (
                              <WhatsAppWhiteIcon />
                            ) : (
                              <DeviceIcon />
                            )}
                          </Box>
                          <Box ml="10px" w="100%">
                            <Text fontSize={"18px"} fontWeight={400}>
                              {device.friendlyname === ""
                                ? device.model
                                : device.friendlyname.replace(/\+/g, " ")}
                            </Text>
                            {device.lasteventtime > 0 && (
                              <Text color={"#90a4ae"} fontWeight={300}>
                                {`${translate["lastdevice"]} ${NumberToTimeText(
                                  device.lasteventtime
                                )}`}
                              </Text>
                            )}
                          </Box>
                        </Box>
                        <Box
                          ml="10px"
                          display={"flex"}
                          h={"100%"}
                          alignItems={"center"}
                        >
                          {device.imei === currentDevice.imei && (
                            <SuccessGreenIcon />
                          )}
                        </Box>
                      </Box>
                    );
                  })}
              {/* <Box
                display={"flex"}
                alignItems={"center"}
                p="20px"
                borderRadius={"4px"}
                boxShadow={"0 2px 9px 0 rgba(169, 184, 200, .2) !important"}
                onClick={() => {
                  dispatch(
                    markUI({
                      registerCurrentDeviceForm: "Android",
                    })
                  );
                  if (pathname === "registerNewDevice") {
                  } else {
                    navigate("/registerNewDevice");
                  }
                  onClose();
                }}
                _hover={{ cursor: "pointer" }}
              >
                <Box
                  minW={"40px"}
                  h={"40px"}
                  bgColor={"green"}
                  borderRadius={"50px"}
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"center"}
                >
                  <AddIcon />
                </Box>
                <Box ml="10px">
                  <Text fontSize={"18px"} fontWeight={400}>
                    Android
                  </Text>
                </Box>
              </Box>

              {/* <Box
                display={"flex"}
                alignItems={"center"}
                p="20px"
                borderRadius={"4px"}
                boxShadow={"0 2px 9px 0 rgba(169, 184, 200, .2) !important"}
                onClick={() => {
                  dispatch(
                    markUI({
                      registerCurrentDeviceForm: "IOS",
                    })
                  );
                  if (pathname === "/registerNewDevice") {
                  } else {
                    navigate("/registerNewDevice");
                  }
                  onClose();
                }}
                _hover={{ cursor: "pointer" }}
              >
                <Box
                  minW={"40px"}
                  h={"40px"}
                  bgColor={`${colorThemeMode}_secondary`}
                  borderRadius={"50px"}
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"center"}
                >
                  <AddIcon />
                </Box>
                <Box ml="10px">
                  <Text fontSize={"18px"} fontWeight={400}>
                    iOS
                  </Text>
                </Box>
              </Box>
              <Box
                display={"flex"}
                alignItems={"center"}
                p="20px"
                borderRadius={"4px"}
                boxShadow={"0 2px 9px 0 rgba(169, 184, 200, .2) !important"}
                onClick={() => {
                  dispatch(
                    markUI({
                      whatsappwebchat: true,
                      registerCurrentDeviceForm: "whatsappWeb",
                    })
                  );
                  if (pathname === "/registerNewDevice") {
                  } else {
                    navigate("/registerNewDevice");
                  }
                  onClose();
                }}
                _hover={{ cursor: "pointer" }}
              >
                <Box
                  minW={"40px"}
                  h={"40px"}
                  bgColor={"green"}
                  borderRadius={"50px"}
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"center"}
                >
                  <AddIcon />
                </Box>
                <Box ml="10px">
                  <Text fontSize={"18px"} fontWeight={400}>
                    Whatsapp Web
                  </Text>
                </Box>
              </Box> */}
            </ModalBody>
          </ModalContent>
        </Modal>
      </>
    );
  } else {
    return (
      <>
        <Button
          className="deviceSelect"
          width={"fit-content"}
          onClick={onOpen}
          p={{ base: "5px 20px", md: "5px 10px", lg: "5px 20px" }}
          h="50px"
          borderRadius={"0px"}
          _hover={{ bg: "#00000020" }}
          maxW={{ base: "auto", lg: "200px" }}
          overflow={"hidden"}
          display={"flex"}
          alignItems={"center"}
        >
          <MobileRedIcon />
        </Button>
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
          <ModalOverlay />
          <ModalContent
            mx={{ base: "20px", sxm: "0px" }}
            minW={{
              base: "90% !important",
              sxm: "500px !important",
              lg: "798px !important",
            }}
          >
            <ModalHeader
              borderRadius={"4px 4px 0px 0px"}
              bg={`${colorThemeMode}_background`}
              color={`${colorThemeMode}_text`}
              borderBottom={"1px solid"}
              borderColor={`${colorThemeMode}_tertiary`}
            >
              <Text fontSize={"24px"} fontWeight={"400"}>
                {translate["registereddevices"]}
              </Text>
            </ModalHeader>
            <ModalCloseButton color={`${colorThemeMode}_text`} />
            {/* <ModalBody
              borderRadius={"0px 0px 4px 4px"}
              bg={`${colorThemeMode}_background`}
              color={`${colorThemeMode}_text`}
              p="16px"
            >
              <Box
                display={"flex"}
                alignItems={"center"}
                p="20px"
                borderRadius={"4px"}
                boxShadow={"0 2px 9px 0 rgba(169, 184, 200, .2) !important"}
                onClick={() => {
                  dispatch(
                    markUI({
                      registerCurrentDeviceForm: "Android",
                    })
                  );
                  if (pathname === "registerNewDevice") {
                  } else {
                    navigate("/registerNewDevice");
                  }
                  onClose();
                }}
                _hover={{ cursor: "pointer" }}
              >
                <Box
                  minW={"40px"}
                  h={"40px"}
                  bgColor={"green"}
                  borderRadius={"50px"}
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"center"}
                >
                  <AddIcon />
                </Box>
                <Box ml="10px">
                  <Text fontSize={"18px"} fontWeight={400}>
                    Android
                  </Text>
                </Box>
              </Box>

              <Box
                display={"flex"}
                alignItems={"center"}
                p="20px"
                borderRadius={"4px"}
                boxShadow={"0 2px 9px 0 rgba(169, 184, 200, .2) !important"}
                onClick={() => {
                  dispatch(
                    markUI({
                      registerCurrentDeviceForm: "IOS",
                    })
                  );
                  if (pathname === "/registerNewDevice") {
                  } else {
                    navigate("/registerNewDevice");
                  }
                  onClose();
                }}
                _hover={{ cursor: "pointer" }}
              >
                <Box
                  minW={"40px"}
                  h={"40px"}
                  bgColor={`${colorThemeMode}_secondary`}
                  borderRadius={"50px"}
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"center"}
                >
                  <AddIcon />
                </Box>
                <Box ml="10px">
                  <Text fontSize={"18px"} fontWeight={400}>
                    iOS
                  </Text>
                </Box>
              </Box>
              <Box
                display={"flex"}
                alignItems={"center"}
                p="20px"
                borderRadius={"4px"}
                boxShadow={"0 2px 9px 0 rgba(169, 184, 200, .2) !important"}
                onClick={() => {
                  dispatch(
                    markUI({
                      registerCurrentDeviceForm: "whatsappWeb",
                      whatsappwebchat: true
                    })
                  );
                  if (pathname === "/registerNewDevice") {
                  } else {
                    navigate("/registerNewDevice");
                  }
                  onClose();
                }}
                _hover={{ cursor: "pointer" }}
              >
                <Box
                  minW={"40px"}
                  h={"40px"}
                  bgColor={"green"}
                  borderRadius={"50px"}
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"center"}
                >
                  <AddIcon />
                </Box>
                <Box ml="10px">
                  <Text fontSize={"18px"} fontWeight={400}>
                    Whatsapp Web
                  </Text>
                </Box>
              </Box>
            </ModalBody> */}
          </ModalContent>
        </Modal>
      </>
    );
  }
};

export default DeviceSelectionModal;
